import React from 'react';
import PropTypes from 'prop-types';
import {
  DEFAULT_FIELDS_TO_REMOVE,
  FIELDS_TO_REMOVE,
  FIELD_TYPES,
  GDPR_SETTING,
  IMPLIED_GDPR_SETTINGS,
  LEGACY_CONSENT_FIELD_NAMES,
  EXPLICIT_CONSENT_FIELD_NAMES,
} from 'utils/constants';
import {
  ERROR_TEXT_CHECKED_FIELD_NO_TEXT,
  ERROR_TEXT_EMPTY_REQUIRED_FIELD,
  ERROR_TEXT_NO_DEFAULT_VALUE,
  errorTextImpliedHasInvalidField,
} from 'langs/en/errors';
import InputContainer from 'components/InputContainer';
import SelectAttribute from 'components/SelectAttribute';
import TextAreaAttribute from '../TextAreaAttribute';
import CheckBoxAttribute from '../CheckBoxAttribute';
import RulesContainer from '../RulesContainer';
import FieldCommonOverrideEditor from '../FieldCommonOverrideEditor';

export default class FieldCommonEditor extends React.Component {
  constructor(props) {
    super(props);
    this.credentialType = props.availableCredentialTypes?.[0]?.value;
  }

  validateAndChangeRequiredFieldValue = (keyPath, value) => {
    this.props.onFieldValueChange(keyPath, value);

    if (value.length === 0) {
      this.props.fieldValidityChanged(keyPath, false, ERROR_TEXT_EMPTY_REQUIRED_FIELD);
    } else {
      this.props.fieldValidityChanged(keyPath, true);
    }
  }

  validateAndChangeHelpText = (keyPath, value) => {
    this.props.onFieldValueChange(keyPath, value);

    if (!value.trim()) {
      this.props.fieldValidityChanged(keyPath, false, ERROR_TEXT_CHECKED_FIELD_NO_TEXT);
    } else {
      this.props.fieldValidityChanged(keyPath, true);
    }
  }

  validateAndChangeHelpTextCheckbox = (keyPath, value) => {
    this.props.onFieldValueChange(keyPath, value);
    const helpTextTextAreaKeyPath = [...this.props.keyPath, 'helpText', 'value'];

    if (value) {
      this.props.fieldValidityChanged(helpTextTextAreaKeyPath, false, ERROR_TEXT_CHECKED_FIELD_NO_TEXT);
    } else {
      this.props.fieldValidityChanged(helpTextTextAreaKeyPath, true);
    }
  }

  validateAndChangeRulesContainerValue = (keyPath, value) => {
    this.props.onFieldValueChange(keyPath, value);
    if (keyPath[keyPath.length - 1] === 'hidden') {
      const defaultValueKeyPath = [...this.props.keyPath, 'editors'];
      let currentDefaultValue;
      switch (this.props.values.type) {
        case FIELD_TYPES.DROPDOWN:
          defaultValueKeyPath.push('dropdownConfig', 'defaultOption');
          currentDefaultValue = this.props.values.editors.dropdownConfig.defaultOption;
          break;
        case FIELD_TYPES.RADIO_BUTTON:
          defaultValueKeyPath.push('radioButtonConfig', 'defaultOption');
          currentDefaultValue = this.props.values.editors.radioButtonConfig.defaultOption;
          break;
        default:
          defaultValueKeyPath.push('value');
          currentDefaultValue = this.props.values.editors.value;
          break;
      }
      if (value && currentDefaultValue.length === 0) {
        this.props.fieldValidityChanged(defaultValueKeyPath, false, ERROR_TEXT_NO_DEFAULT_VALUE);
      } else {
        this.props.fieldValidityChanged(defaultValueKeyPath, true);
      }

      this.props.onFieldUIStateChange(defaultValueKeyPath, { required: value });
    }
  }

  validateAndChangeName = (keyPath, value) => {
    this.props.onFieldNameChange(keyPath.slice(0, -1), value);
    if (!value) {
      this.props.fieldValidityChanged(keyPath, false, ERROR_TEXT_EMPTY_REQUIRED_FIELD);
      return;
    }
    const isImpliedInvalid = IMPLIED_GDPR_SETTINGS.includes(this.props.gdprSetting) && LEGACY_CONSENT_FIELD_NAMES.includes(value);
    const isAlwaysInvalid = (this.props.gdprSetting === GDPR_SETTING.ALWAYS_IMPLIED && EXPLICIT_CONSENT_FIELD_NAMES.includes(value));

    if (isImpliedInvalid || isAlwaysInvalid) {
      this.props.fieldValidityChanged(keyPath, false, errorTextImpliedHasInvalidField(value, this.credentialType), true);
    } else {
      this.props.fieldValidityChanged(keyPath, true);
    }
  }

  filterFieldNameDropdownOpts = () => {
    if (this.props.supportsDegreeOffering) return this.props.fieldNameDropdownOpts;

    const fieldsToRemove = FIELDS_TO_REMOVE[this.credentialType] || DEFAULT_FIELDS_TO_REMOVE;
    return Object.keys(this.props.fieldNameDropdownOpts)
      .filter((key) => !fieldsToRemove.has(key))
      .reduce((res, key) => {
        res[key] = this.props.fieldNameDropdownOpts[key];
        return res;
      }, {});
  }

  render() {
    const fieldNameLabel = `Standard Field Name${this.props.loadInProgress ? ' Loading...' : ''}`;
    const standardFieldNameIsNull = !this.props.values.name;
    const { hasHelpText } = this.props.values.helpText;

    return (
      <div className="col-11 mt-2 pt-3">
        <div className="row mb-4">
          <div className="col-sm-6">
            <InputContainer
              label={fieldNameLabel}
              keyPath={[...this.props.keyPath, 'name']}
              uiState={this.props.uiState.name}
              disabled={this.props.loadInProgress || this.props.requiredStandardField || this.props.isBranchingField || this.props.isThankYouField}
            >
              <SelectAttribute
                disableNullOption
                keyPath={[...this.props.keyPath, 'name']}
                value={this.props.values.name}
                autofocus={standardFieldNameIsNull}
                options={this.filterFieldNameDropdownOpts()}
                onFieldValueChange={this.validateAndChangeName}
                onFieldUIStateChange={this.props.onFieldUIStateChange}
                sortOptions
              />
            </InputContainer>
            <div className="text-muted small mt-2">{`SF type: ${this.props.values.name ? this.props.sfDetails.sfFieldType : ''}`}</div>
          </div>
          <div className="col-sm-6">
            <InputContainer
              label="Display Name"
              keyPath={[...this.props.keyPath, 'label']}
              uiState={this.props.uiState.label}
              disabled={standardFieldNameIsNull}
            >
              <TextAreaAttribute
                keyPath={[...this.props.keyPath, 'label']}
                value={this.props.values.label}
                disabled={standardFieldNameIsNull}
                onFieldValueChange={this.validateAndChangeRequiredFieldValue}
                onFieldUIStateChange={this.props.onFieldUIStateChange}
              />
            </InputContainer>
          </div>
        </div>
        <div className="row mb-2">
          <div className="col-sm-6">
            <CheckBoxAttribute
              labelClassName="mb-0"
              fieldType="tx-form"
              keyPath={[...this.props.keyPath, 'helpText', 'hasHelpText']}
              value={hasHelpText}
              onCheckBoxChange={this.validateAndChangeHelpTextCheckbox}
            >
              <p className="mb-0 font-weight-bold">Include help text (optional)</p>
            </CheckBoxAttribute>
          </div>
        </div>
        {hasHelpText ? (
          <div className="row mb-4">
            <div className="col">
              <InputContainer
                keyPath={[...this.props.keyPath, 'helpText', 'value']}
                uiState={this.props.uiState.helpText.value}
              >
                <TextAreaAttribute
                  keyPath={[...this.props.keyPath, 'helpText', 'value']}
                  value={this.props.values.helpText.value}
                  onFieldValueChange={this.validateAndChangeHelpText}
                  onFieldUIStateChange={this.props.onFieldUIStateChange}
                  placeholder="The copy in this field will appear below the field as helpText."
                  className="pt-0"
                />
              </InputContainer>
            </div>
          </div>
        ) : null}
        <div className="row mb-4">
          <div className="col-sm-6">
            <RulesContainer
              disabled={this.props.disableRulesContainer}
              keyPath={this.props.keyPath}
              onFieldValueChange={this.validateAndChangeRulesContainerValue}
              radioGroupName="Rules"
              requiredStandardField={this.props.requiredStandardField}
              values={this.props.values}
            />
          </div>
        </div>
        {this.props.showOverrideEditor ? (
          <FieldCommonOverrideEditor
            keyPath={this.props.keyPath}
            uiState={this.props.uiState}
            values={this.props.values}
            validateAndChangeRequiredFieldValue={this.validateAndChangeRequiredFieldValue}
            validateAndChangeRulesContainerValue={this.validateAndChangeRulesContainerValue}
            onFieldUIStateChange={this.props.onFieldUIStateChange}
            onFieldValueChange={this.props.onFieldValueChange}
            standardFieldNameIsNull={standardFieldNameIsNull}
            disableRulesContainer
            requiredStandardField={this.props.requiredStandardField}
          />
        ) : null}
      </div>
    );
  }
}

FieldCommonEditor.propTypes = {
  disableRulesContainer: PropTypes.bool.isRequired,
  fieldValidityChanged: PropTypes.func.isRequired,
  fieldNameDropdownOpts: PropTypes.object.isRequired,
  gdprSetting: PropTypes.string.isRequired,
  isBranchingField: PropTypes.bool.isRequired,
  showOverrideEditor: PropTypes.bool,
  isThankYouField: PropTypes.bool,
  keyPath: PropTypes.array.isRequired,
  loadInProgress: PropTypes.bool,
  onFieldNameChange: PropTypes.func.isRequired,
  onFieldUIStateChange: PropTypes.func.isRequired,
  onFieldValueChange: PropTypes.func.isRequired,
  requiredStandardField: PropTypes.bool,
  sfDetails: PropTypes.object.isRequired,
  supportsDegreeOffering: PropTypes.bool.isRequired,
  availableCredentialTypes: PropTypes.array,
  uiState: PropTypes.shape({
    label: PropTypes.object,
    name: PropTypes.object,
    helpText: PropTypes.object,
    optInData: PropTypes.object,
    gdprConsentData: PropTypes.object,
  }),
  values: PropTypes.shape({
    editors: PropTypes.shape({
      degreeOfferingConfig: PropTypes.object,
      dropdownConfig: PropTypes.object,
      optInData: PropTypes.object,
      gdprConsentData: PropTypes.object,
      radioButtonConfig: PropTypes.object,
      value: PropTypes.string,
    }).isRequired,
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired,
    showOverrideEditor: PropTypes.bool,
    hidden: PropTypes.bool.isRequired,
    flags: PropTypes.arrayOf(PropTypes.string),
    helpText: PropTypes.object,
    overrides: PropTypes.object,
  }),
};

FieldCommonEditor.defaultProps = {
  loadInProgress: false,
  isThankYouField: false,
  requiredStandardField: false,
  availableCredentialTypes: [],
  uiState: {},
  showOverrideEditor: false,
  values: {
    flags: [],
    overrides: {},
    showOverrideEditor: false,
  },
};
